import React from 'react';
import { motion } from 'framer-motion';
import ThemeSwitcher from './components/ThemeSwitcher';
import ScrollFloat from './components/ScrollFloat';

function AnimatedText({ text }) {
  const letters = Array.from(text);

  const container = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { staggerChildren: 0.03, delayChildren: 0.04 * i },
    }),
  };

  const child = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
    hidden: {
      opacity: 0,
      y: 20,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
      },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="flex overflow-hidden"
    >
      {letters.map((letter, index) => (
        <motion.span
          variants={child}
          key={index}
          className={letter === " " ? "mr-2" : ""}
        >
          {letter}
        </motion.span>
      ))}
    </motion.div>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-primary-dark dark:bg-primary text-primary-dark dark:text-primary">
      {/* Navigation */}
      <nav className="bg-primary-dark dark:bg-primary p-4 sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#00B8E4] rounded-lg flex items-center justify-center mr-2">
              <span className="text-[#0F1419] font-bold">K</span>
            </div>
            <a href="/" className="text-[#00B8E4] text-2xl font-bold">
              <AnimatedText text="Keury David" />
            </a>
          </div>

          {/* Navigation links with correct section IDs */}
          <div className="flex items-center space-x-6">
            <a href="#proyectos" className="text-gray-300 hover:text-white transition-colors">Projects</a>
            <a href="#sobre-mi" className="text-gray-300 hover:text-white transition-colors">About me</a>
            <a href="#contacto" className="text-gray-300 hover:text-white transition-colors">Contact</a>
            <ThemeSwitcher />
          </div>
        </div>
      </nav>

      {/* Hero Section with Profile */}
      <section className="container mx-auto px-4 py-16">
        {/* Profile Info */}
        <div className="flex flex-col items-center relative z-10">
          <div className="w-40 h-40 rounded-full border-4 border-white overflow-hidden bg-[#0F1419] ring-4 ring-[#0F1419]">
            <img
              src="/images/graduation-photo.jpg"
              alt="Keury David Profile"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-4xl font-bold mt-4">Keury David</h1>
          <p className="text-[#00B8E4] text-xl">@keurydl</p>

          {/* Social Icons */}
          <div className="flex space-x-6 mt-6">
            <a href="https://github.com/Keurydl" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#00B8E4] transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="https://www.instagram.com/_keurydd_/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#00B8E4] transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-3.96-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.048 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="https://www.linkedin.com/in/keury-david-deschamps-lopez-3891181b1/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#00B8E4] transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          {/* Sobre mí section */}
          <section id="sobre-mi" className="mb-16">
            <ScrollFloat
              animationDuration={1}
              ease='back.inOut(2)'
              scrollStart='center bottom+=50%'
              scrollEnd='bottom bottom-=40%'
              stagger={0.03}
              containerClassName="mb-6"
            >
              About Me
            </ScrollFloat>
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="flex-1">
                <p className="text-gray-400 text-lg">
                  I'm a passionate software developer with experience in web and app development. I specialize in technologies like React, Python, and modern web app development (like Flutter and Dart).
                </p>
              </div>
              <div className="w-full md:w-96 rounded-lg overflow-hidden">
                <img
                  src="https://media.giphy.com/media/qgQUggAC3Pfv687qPC/giphy.gif"
                  alt="Coding animation"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </section>

          {/* Lenguajes destacados section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Featured languages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* JavaScript Card remains the same */}
              <a href="#" className="bg-[#1A1F24] hover:bg-[#1E2328] transition-colors rounded-lg p-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-[#F7DF1E] p-4 rounded-lg w-16 h-16 flex items-center justify-center">
                    <span className="text-black text-3xl font-bold">JS</span>
                  </div>
                  <div>
                    <h3 className="text-xl text-white font-semibold">JavaScript</h3>
                    <p className="text-gray-400">JavaScript</p>
                  </div>
                </div>
              </a>

              {/* Python Card with updated logo */}
              <a href="#" className="bg-[#1A1F24] hover:bg-[#1E2328] transition-colors rounded-lg p-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-white p-3 rounded-lg w-16 h-16 flex items-center justify-center">
                    <img
                      src="/images/Python-logo.png"
                      alt="Python Logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl text-white font-semibold">Python</h3>
                    <p className="text-gray-400">Python</p>
                  </div>
                </div>
              </a>
            </div>
          </section>

          {/* Proyectos section */}
          <section id="proyectos" className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Audio Visualization */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/be9a12f6-7444-4ad7-ae08-c65c88954b8d"
                    alt="Audio Visualization Preview"
                    className="w-full h-56 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/Audio_Visualization"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 3v9.28a4.39 4.39 0 0 0-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Audio Visualization</h3>
                  </div>
                  <p className="text-gray-400 mb-4">An interactive web application that creates stunning visualizations based on audio input using the Web Audio API.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Python</span>

                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/Audio_Visualization" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              {/* Neural Network */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/05de0c54-b012-47b6-8e7b-cea68b672c0b"
                    alt="Neural Network Preview"
                    className="w-full h-56 object-contain bg-[#1E2328] p-4"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/neural_newtwork"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 013 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.33-.19.73-.19 1.07 0l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L5 8.09v7.82l7 3.94 7-3.94V8.09l-7-3.94z" />
                        <path d="M12.75 16.5h-1.5v-3.75l-2.25 2.25-1.06-1.06L12 10.06l3.06 3.38-1.06 1.06-2.25-2.25V16.5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Neural Network</h3>
                  </div>
                  <p className="text-gray-400 mb-4">A machine learning project implementing a neural network from scratch for pattern recognition and data analysis.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Python</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">NumPy</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Pandas</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/neural_newtwork" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              {/* App de Videojuegos */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative flex flex-col items-center justify-center pt-6">
                  <img
                    src="/images/app_videogames.png"
                    alt="App de Videojuegos"
                    className="w-full h-56 object-contain bg-[#00B8E4] p-4"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/app_videogames"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6 flex flex-col items-center">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#00B8E4] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M5 8V6a5 5 0 0 1 10 0v2h1a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3h1zm2-2a3 3 0 0 1 6 0v2H7V6zm-3 5v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1zm5 2a1 1 0 1 1 2 0v2a1 1 0 1 1-2 0v-2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Video game app/news app</h3>
                  </div>
                  <p className="text-gray-400 mb-4 text-center">Web application that shows news and video game games using the RAWG API. It allows you to see games, news and offline mode.</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Dart</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">RAWG API</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-2">
                    <a href="https://github.com/Keurydl/app_videogames" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              {/* React Calculator */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/73f9de85-2bbc-42d6-88d7-cfafb7de7b11"
                    alt="React Calculator Preview"
                    className="w-full h-56 object-contain bg-[#1E2328] p-4"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/Calculadora"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 5.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 1.576 5.512 1.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 13 6.001 13z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">React Calculator</h3>
                  </div>
                  <p className="text-gray-400 mb-4">A modern calculator application built with React, featuring a clean UI and responsive design.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">React</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">JavaScript</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CSS</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/Calculadora" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                    <a href="https://incomparable-salmiakki-6110cb.netlify.app/" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Web Mathematics */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/e68a16fb-7a44-4a43-85cd-cc34cc24803a"
                    alt="Web Mathematics Preview"
                    className="w-full h-56 object-contain bg-[#1E2328] p-4"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/web_mathematics"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm6 9.09c0 4-2.55 7.7-6 8.83-3.45-1.13-6-4.82-6-8.83v-4.7l6-2.25 6 2.25v4.7z" />
                        <path d="M9.91 11.5h4.18c-.38 1.29-1.52 2.19-2.91 2.19v-1.19c.69 0 1.27-.45 1.48-1.07h-2.75v-1.5h2.75c-.21-.62-.79-1.07-1.48-1.07V8c1.39 0 2.53.9 2.91 2.19h1.55C16.17 8.12 14.21 6.5 12 6.5s-4.17 1.62-4.64 3.69h1.55c.38-1.29 1.52-2.19 2.91-2.19v1.19c-.69 0-1.27.45-1.48 1.07h2.75v1.5h-2.75c.21.62.79 1.07 1.48 1.07v1.19c-1.39 0-2.53-.9-2.91-2.19H7.36C7.83 14.38 9.79 16 12 16s4.17-1.62 4.64-3.69h-1.55c-.38 1.29-1.52 2.19-2.91 2.19v-1.19c.69 0 1.27-.45 1.48-1.07h-2.75z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Web Mathematics</h3>
                  </div>
                  <p className="text-gray-400 mb-4">An interactive web application for learning and practicing various mathematical concepts and calculations.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">JavaScript</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Python</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CSS</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/web_mathematics" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Sistema de Pedidos */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/b09190a3-4896-43ae-b451-11265c2f1020"
                    alt="Sistema de Pedidos Preview"
                    className="w-full h-56 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/sistema_pedidos"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Sistema de Pedidos</h3>
                  </div>
                  <p className="text-gray-400 mb-4">A comprehensive order management system for tracking and processing customer orders efficiently.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">PHP</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">MySQL</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">HTML</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CSS</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/sistema_pedidos" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Tic Tac Toe */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="https://github.com/user-attachments/assets/6d45ee88-cfc4-48be-9dfa-f30272050ece"
                    alt="Tic Tac Toe Preview"
                    className="w-full h-56 object-contain bg-[#1E2328] p-4"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/Tic_Tac_Toe"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Code
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 6h12v12H6V6zm2 2v8h8V8H8z" />
                        <path d="M10 10h4v4h-4v-4z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Tic Tac Toe</h3>
                  </div>
                  <p className="text-gray-400 mb-4">A classic Tic Tac Toe game built with modern web technologies, featuring a clean and responsive interface.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">React</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">JavaScript</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CSS</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/Tic_Tac_Toe" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Batería Electrónica */}
              {/* Batería Electrónica */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="/images/Bateria_electronica.png"
                    alt="Batería Electrónica Preview"
                    className="w-full h-56 object-contain bg-[#1E2328]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://peppy-axolotl-d5fe82.netlify.app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Demo
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 3v9.28a4.39 4.39 0 0 0-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Electronic Drums</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Interactive application that allows you to create rhythms and percussion patterns intuitively.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">JavaScript</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">HTML</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CSS</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/bateria_electronica" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                    <a href="https://peppy-axolotl-d5fe82.netlify.app/" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Random Password Generator */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="/images/Random_Password.png"
                    alt="Random Password Generator Preview"
                    className="w-full h-56 object-contain bg-[#1E2328]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/Random_Password"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Demo
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 17a2 2 0 100-4 2 2 0 000 4zm6-9a6 6 0 00-12 0v2h12V8zM6 8v2h12V8a6 6 0 10-12 0zm14 2H4a2 2 0 00-2 2v8a2 2 0 002 2h16a2 2 0 002-2v-8a2 2 0 00-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Random Password</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Modern desktop application for generating secure random passwords.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Python</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Tkinter</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/Random_Password" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Programa-C */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="/images/Proyecto_en_c.png"
                    alt="Programa C Preview"
                    className="w-full h-56 object-contain bg-[#1E2328]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/Programa-C"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Demo
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4V8h16v10zm-2-1h-6v-2h6v2zM7.5 17l-1.41-1.41L8.67 13l-2.58-2.59L7.5 9l4 4-4 4z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">Program-C</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Project developed in C that demonstrates fundamental programming concepts.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">C</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Algoritmos</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/Programa-C" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* CrudCanina */}
              <div className="bg-[#1A1F24] rounded-lg overflow-hidden group hover:bg-[#1E2328] transition-colors">
                <div className="relative">
                  <img
                    src="/images/Crud.png"
                    alt="CRUD Canina Preview"
                    className="w-full h-56 object-contain bg-[#1E2328]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href="https://github.com/Keurydl/CrudCanina"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium px-4 py-2 bg-[#3B82F6] rounded-full hover:bg-[#2563EB] transition-colors">
                      View Demo
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8v-6h-8v6zm0-8h8V3h-8v10z" />
                      </svg>
                    </div>
                    <h3 className="text-xl text-white font-semibold">CrudCanina</h3>
                  </div>
                  <p className="text-gray-400 mb-4">CRUD system for canine business management developed in Java.</p>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">Java</span>
                    <span className="text-sm px-2 py-1 bg-[#2D333B] rounded text-gray-300">CRUD</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-4">
                    <a href="https://github.com/Keurydl/CrudCanina" target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Formación */}
          <section id="formacion" className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Training</h2>
            <div className="space-y-4">
              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white">Dominican University O&M</h3>
                    <p className="text-gray-400 mt-1">Studying Systems and Computer Engineering</p>
                    <span className="text-[#3B82F6] text-sm mt-2 inline-block">Current situation</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Pontificia Universidad Católica Madre y Maestra (PUCMM)</h3>
                      <a href="https://drive.google.com/file/d/1yWMBb0LobGhy4V3ZTn6_nuZhRY3OXwot/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#3B82F6] hover:text-[#60A5FA] transition-colors ml-4 flex items-center">
                        <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 0h-2v2h2v2h-2v2h2v-5a2 2 0 0 0-2-2 2 2 0 0 0 2-2V5h-2V3h2z" />
                        </svg>
                        <span className="text-sm">View certificate</span>
                      </a>
                    </div>
                    <p className="text-gray-400 mt-1">Studying Multiplatform Applications at the Center for Technology and Continuing Education (TEP)</p>
                    <span className="text-[#3B82F6] text-sm mt-2 inline-block">2023-2025</span>
                  </div>
                </div>
              </div>

              {/* Colegio */}
              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Colegio Episcopal La Anunciación</h3>
                      <a href="https://drive.google.com/file/d/15HCgDslFFD1HrW78Jig2P6KDYdQ3KqLm/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#3B82F6] hover:text-[#60A5FA] transition-colors ml-4 flex items-center">
                        <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 0h-2v2h2v2h-2v2h2v-5a2 2 0 0 0-2-2 2 2 0 0 0 2-2V5h-2V3h2z" />
                        </svg>
                        <span className="text-sm">View certificate</span>
                      </a>
                    </div>
                    <p className="text-gray-400 mt-1">Graduated from 6th grade of Secondary School</p>
                    <span className="text-[#3B82F6] text-sm mt-2 inline-block">2011-2023</span>
                  </div>
                </div>
              </div>

              {/* Centro Dominico Americano */}
              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Centro Dominico Americano</h3>
                      <a href="https://drive.google.com/file/d/15sfC2Rg0Rom6j4efTm1yhA7iBDQa1oPL/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#3B82F6] hover:text-[#60A5FA] transition-colors ml-4 flex items-center">
                        <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 0h-2v2h2v2h-2v2h2v-5a2 2 0 0 0-2-2 2 2 0 0 0 2-2V5h-2V3h2z" />
                        </svg>
                        <span className="text-sm">View certificate</span>
                      </a>
                    </div>
                    <p className="text-gray-400 mt-1">English Course</p>
                    <span className="text-[#3B82F6] text-sm mt-2 inline-block">2017-2025</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contacto section */}
          <section id="contacto" className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Contact</h2>
            <div className="space-y-4">
              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white">Email</h3>
                    <a href="mailto:keurydd@outlook.com" className="text-gray-400 hover:text-[#3B82F6] transition-colors mt-1 inline-block">
                      keurydd@outlook.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A1F24] p-6 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.5l1.5-1.5L12 16.5l4.5-4.5 1.5 1.5-6 6-4-4z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white">Curriculum Vitae</h3>
                    <a
                      href="https://drive.google.com/file/d/1xeNtMRjaLW852CpAHyENF3Kys84Sw9oO/view?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#3B82F6] transition-colors mt-1"
                    >
                      <span>View CV</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002 2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-[#1A1F24] py-8">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <p> {new Date().getFullYear()} Keury David. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
